import {Issue} from "../../domain/entities/issue.entity";
import {CrosspointDataService, Viewport, Axis} from "@o2d/datasource";
import {container} from "tsyringe";
import {ViewportQueryClient} from "../../connectivity/viewport-query-client";
import type {
    RestApiCrosspointDataServiceConfig,
    ViewportQueryRequest,
    ViewportQueryResponse,
    AxisQueryConfig,
    EntityAxisConfig,
    AppliedSort
} from "../../connectivity/viewport-query.types";
import {TransformerHelper} from "../../helpers/transformer.helper";
import type {IssueDTO} from "../../domain/dtos/issue.dto";
import {TimelineLoadTracker} from "../../services/timeline-load-tracker.service";

/**
 * Custom error for reference entity not found (404).
 */
export class ReferenceNotFoundError extends Error {
    constructor(public readonly entityType: string, public readonly uid: string) {
        super(`Reference entity ${entityType}/${uid} not found`);
        this.name = 'ReferenceNotFoundError';
    }
}

/**
 * Custom error for viewport query failures.
 */
export class ViewportQueryError extends Error {
    constructor(
        message: string,
        public readonly code: number,
        public readonly recoverable: boolean = false
    ) {
        super(message);
        this.name = 'ViewportQueryError';
    }
}

/**
 * Retry configuration for failed requests.
 */
const RETRY_CONFIG = {
    maxRetries: 3,
    baseDelayMs: 500,
    maxDelayMs: 5000,
} as const;

/**
 * Waits `ms`, or rejects with an AbortError as soon as `signal` is aborted — at once if it already is.
 */
function abortableDelay(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        const aborted = () => new DOMException('Aborted', 'AbortError');
        if (signal.aborted) {
            reject(aborted());
            return;
        }
        const onAbort = () => {
            clearTimeout(timeout);
            reject(aborted());
        };
        const timeout = setTimeout(() => {
            signal.removeEventListener('abort', onAbort);
            resolve();
        }, ms);
        signal.addEventListener('abort', onAbort, {once: true});
    });
}

/**
 * Retry helper with exponential backoff. The back-off wait ends with an AbortError as soon as
 * `signal` is aborted, so a superseded request neither sleeps on nor retries.
 */
async function withRetry<T>(
    fn: () => Promise<T>,
    shouldRetry: (error: unknown) => boolean,
    signal: AbortSignal
): Promise<T> {
    let lastError: unknown;
    for (let attempt = 0; attempt < RETRY_CONFIG.maxRetries; attempt++) {
        try {
            return await fn();
        } catch (error) {
            lastError = error;
            if (!shouldRetry(error)) throw error;
            // No wait after the last attempt: nothing follows it, and the wait would keep the
            // boards' loading bar on with no request open.
            if (attempt === RETRY_CONFIG.maxRetries - 1) break;
            const delay = Math.min(
                RETRY_CONFIG.baseDelayMs * Math.pow(2, attempt),
                RETRY_CONFIG.maxDelayMs
            );
            await abortableDelay(delay, signal);
        }
    }
    throw lastError;
}

/**
 * REST API-based CrosspointDataService for fetching Issues from backend.
 * Implements viewport-based data loading with configurable axes.
 */
export class RestApiCrosspointDataService<TXValue = unknown, TYValue = unknown> extends EventTarget implements CrosspointDataService<Issue> {

    private readonly viewportQueryClient: ViewportQueryClient;
    private readonly loadTracker: TimelineLoadTracker;
    private abortController: AbortController | null = null;

    constructor(
        private readonly config: RestApiCrosspointDataServiceConfig,
        private readonly xAxis: Axis<TXValue, any>,
        private readonly yAxis: Axis<TYValue, any>
    ) {
        super();
        this.viewportQueryClient = container.resolve(ViewportQueryClient);
        this.loadTracker = container.resolve(TimelineLoadTracker);
    }

    /**
     * Fetches issues for the given viewport from backend.
     */
    async fetchData(viewport: Viewport): Promise<Issue[]> {
        // 0. Skip degenerate viewports. During an axis swap (e.g. after a filter
        // clear) the timeline can transiently produce an empty range where
        // minIdx > maxIdx. Such a viewport holds no items, and forwarding it
        // would build an invalid query the backend rejects with HTTP 400.
        if (this.isDegenerateViewport(viewport)) {
            return [];
        }

        // 1. Abort pending requests
        this.cancelPendingRequests();
        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        // 2. Build axis configurations
        const xAxisConfig = this.config.xAxisConfig(this.xAxis, viewport, 'x');
        const yAxisConfig = this.config.yAxisConfig(this.yAxis, viewport, 'y');

        // 3. Build request
        const request: ViewportQueryRequest = {
            xAxis: xAxisConfig,
            yAxis: yAxisConfig,
            includes: this.config.includes
        };

        // 4. Fetch from backend with error handling; the load counter drives the boards' loading
        // bar and is ended however the query settles.
        const endLoad = this.loadTracker.begin();
        try {
            const response = await withRetry(
                () => this.viewportQueryClient.query<IssueDTO>(request, signal),
                (error) => this.isRetryableError(error),
                signal
            );

            // 5. Validate applied sort matches request (Race Condition Mitigation)
            this.validateAppliedSort(request, response);

            // 6. Transform DTOs to Entities
            return await Promise.all(
                response.items.map(dto => TransformerHelper.fromDTO(dto, Issue))
            );
        } catch (error) {
            return this.handleFetchError(error);
        } finally {
            endLoad();
        }
    }

    /**
     * Returns true when either axis range is empty (minIdx > maxIdx).
     */
    private isDegenerateViewport(viewport: Viewport): boolean {
        const [xMin, xMax] = viewport.indexRangeX;
        const [yMin, yMax] = viewport.indexRangeY;
        return xMin > xMax || yMin > yMax;
    }

    /**
     * Cancels any pending requests.
     */
    cancelPendingRequests(): void {
        this.abortController?.abort();
        this.abortController = null;
    }

    /**
     * Determines if an error should trigger a retry.
     */
    private isRetryableError(error: unknown): boolean {
        if (error instanceof DOMException && error.name === 'AbortError') {
            return false;  // Never retry cancelled requests
        }
        if (error instanceof ViewportQueryError) {
            return error.recoverable;
        }
        // Network errors are retryable
        return error instanceof TypeError && error.message.includes('fetch');
    }

    /**
     * Handles fetch errors according to error handling strategy.
     * AbortErrors are propagated to the caller so the data provider
     * does NOT mark the region as successfully fetched.
     */
    private handleFetchError(error: unknown): Issue[] {
        // AbortError: Propagate to caller to prevent false cache entries
        if (error instanceof DOMException && error.name === 'AbortError') {
            throw error;
        }

        // ReferenceNotFoundError: Dispatch event for grid to re-initialize axis
        if (error instanceof ReferenceNotFoundError) {
            this.dispatchEvent(new CustomEvent('axis-reference-invalid', {
                detail: { entityType: error.entityType, uid: error.uid }
            }));
            return [];
        }

        // Other errors: Re-throw for caller to handle
        throw error;
    }

    /**
     * Validates that backend applied the expected sort (Race Condition Mitigation).
     * Dispatches 'sort-mismatch' event if sort differs, allowing grid to re-fetch.
     */
    private validateAppliedSort(
        request: ViewportQueryRequest,
        response: ViewportQueryResponse<IssueDTO>
    ): void {
        if (!response.appliedSort) return;

        const checkAxis = (
            reqAxis: AxisQueryConfig,
            appliedSort: AppliedSort | undefined,
            axisName: 'x' | 'y'
        ) => {
            if (reqAxis.type !== 'Entity' || !appliedSort) return;

            const entityConfig = reqAxis as EntityAxisConfig;
            const hasMismatch =
                appliedSort.strategy !== entityConfig.sort.strategy
                || (entityConfig.sort.strategy === 'property'
                    && (appliedSort.strategy === 'property'
                        && (appliedSort.path !== entityConfig.sort.path
                            || appliedSort.direction !== entityConfig.sort.direction)));

            if (hasMismatch) {
                this.dispatchEvent(new CustomEvent('sort-mismatch', {
                    detail: {
                        axis: axisName,
                        requested: entityConfig.sort,
                        applied: appliedSort
                    }
                }));
            }
        };

        checkAxis(request.xAxis, response.appliedSort.xAxis, 'x');
        checkAxis(request.yAxis, response.appliedSort.yAxis, 'y');
    }
}